/**
 * Tiny sfxr-style synthesiser (issue #76): every sound effect is a handful of parameters stored in the
 * repository and rendered to samples when the game loads, so there are no audio files to license. Pure and
 * deterministic: the noise comes from a seeded generator.
 */
export type Wave = 'square' | 'saw' | 'sine' | 'noise';

export interface SfxVoice {
  wave: Wave;
  /** Start frequency in Hz and the slide in octaves per second (negative falls). */
  frequency: number;
  slide?: number;
  /** Duty of the square wave, 0..1. */
  duty?: number;
  /** Seconds of attack, sustain and decay: the sum is the length of the voice. */
  attack: number;
  sustain: number;
  decay: number;
  volume: number;
  /** Seconds before the voice starts, to chain notes. */
  delay?: number;
}

export interface SfxParams {
  voices: readonly SfxVoice[];
}

/** Length in seconds of the sound: the end of its last voice. */
export function durationOf(params: SfxParams): number {
  return Math.max(...params.voices.map((voice) => (voice.delay ?? 0) + voice.attack + voice.sustain + voice.decay));
}

function seededNoise(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  };
}

const envelope = (voice: SfxVoice, t: number): number => {
  if (t < voice.attack) return t / voice.attack;
  if (t < voice.attack + voice.sustain) return 1;
  const into = t - voice.attack - voice.sustain;
  return Math.max(0, 1 - into / voice.decay);
};

/** Renders a sound to mono samples in [-1, 1]. */
export function renderSfx(params: SfxParams, sampleRate: number): Float32Array {
  const length = Math.ceil(durationOf(params) * sampleRate);
  const samples = new Float32Array(length);
  params.voices.forEach((voice, index) => {
    const noise = seededNoise(index + 1);
    const start = Math.floor((voice.delay ?? 0) * sampleRate);
    const total = voice.attack + voice.sustain + voice.decay;
    const count = Math.min(length - start, Math.ceil(total * sampleRate));
    let phase = 0;
    let held = noise();
    for (let i = 0; i < count; i++) {
      const t = i / sampleRate;
      const frequency = voice.frequency * 2 ** ((voice.slide ?? 0) * t);
      phase += frequency / sampleRate;
      const cycle = phase % 1;
      let value: number;
      switch (voice.wave) {
        case 'square':
          value = cycle < (voice.duty ?? 0.5) ? 1 : -1;
          break;
        case 'saw':
          value = cycle * 2 - 1;
          break;
        case 'sine':
          value = Math.sin(cycle * Math.PI * 2);
          break;
        case 'noise':
          // Held noise: a new value each cycle, so the frequency sets how rough it is.
          if (cycle < frequency / sampleRate) held = noise();
          value = held;
          break;
      }
      samples[start + i]! += value * envelope(voice, t) * voice.volume;
    }
  });
  for (let i = 0; i < length; i++) samples[i] = Math.max(-1, Math.min(1, samples[i]!));
  return samples;
}
